/**
 * Utilization Chart Component
 * Weekly line chart of UtilizedHours / AvailableHours × 100
 * One line per Worker Type + Procurement Source combo
 * Filterable by Procurement Source (dropdown)
 */

import { useMemo } from 'react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChartExportMenu } from './ChartExportMenu'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import type { UtilizationDataPoint } from '@/types/ui/dashboard'

const LINE_COLORS = [
  '#3b82f6', // blue-500
  '#f97316', // orange-500
  '#22c55e', // green-500
  '#a855f7', // purple-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#eab308', // yellow-500
  '#ec4899', // pink-500
]

interface UtilizationChartProps {
  data: UtilizationDataPoint[]
  isLoading: boolean
  procurementSourceOptions: string[]
  selectedProcurementSource: string | null
  onProcurementSourceChange: (value: string | null) => void
}

export function UtilizationChart({
  data,
  isLoading,
  procurementSourceOptions,
  selectedProcurementSource,
  onProcurementSourceChange,
}: UtilizationChartProps) {
  // Apply frontend filter: procurement source
  const filteredData = useMemo(() => {
    if (!selectedProcurementSource) return data
    return data.filter(p => p.procurementSourceName === selectedProcurementSource)
  }, [data, selectedProcurementSource])

  // Build chart data: one row per week, one series per Worker Type + Procurement Source combo
  const { chartData, chartConfig, seriesKeys } = useMemo(() => {
    const weeks = Array.from(new Set(filteredData.map(d => d.weekStartDate))).sort()

    // One series per unique workerType + procurementSource combo
    const seriesKeys = Array.from(
      new Set(filteredData.map(d => {
        const wt = d.workerProfileTypeName ?? 'Unknown'
        const ps = d.procurementSourceName
        return ps ? `${wt} \u2013 ${ps}` : wt
      }))
    ).sort()

    // Lookup: seriesKey → week → utilizationPercent
    const lookup = new Map<string, Map<string, number | null>>()
    for (const point of filteredData) {
      const wt = point.workerProfileTypeName ?? 'Unknown'
      const ps = point.procurementSourceName
      const key = ps ? `${wt} \u2013 ${ps}` : wt
      if (!lookup.has(key)) lookup.set(key, new Map())
      lookup.get(key)!.set(point.weekStartDate, point.utilizationPercent)
    }

    const chartData = weeks.map(week => {
      const row: Record<string, string | number | null> = {
        week: new Date(week + 'T00:00:00Z').toLocaleDateString('default', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      }
      for (const key of seriesKeys) {
        row[key] = lookup.get(key)?.get(week) ?? null
      }
      return row
    })

    const config: ChartConfig = {}
    seriesKeys.forEach((key, i) => {
      config[key] = {
        label: key,
        color: LINE_COLORS[i % LINE_COLORS.length],
      }
    })

    return { chartData, chartConfig: config, seriesKeys }
  }, [filteredData])

  const hasData = chartData.length > 0 && seriesKeys.length > 0

  const handleExportCSV = () => {
    const exportData = chartData.map(row => {
      const out: Record<string, string | number> = { Week: String(row.week) }
      seriesKeys.forEach(key => { out[key] = row[key] ?? '' })
      return out
    })
    exportToCSV(exportData, generateFilename('Utilization', 'portfolio', 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = chartData.map(row => {
      const out: Record<string, string | number> = { Week: String(row.week) }
      seriesKeys.forEach(key => { out[key] = row[key] ?? '' })
      return out
    })
    exportToExcel(exportData, generateFilename('Utilization', 'portfolio', 'xlsx'), 'Utilization')
  }

  const handleExportPNG = () => {
    exportToPNG('utilization-chart', generateFilename('Utilization', 'portfolio', 'png'))
  }

  return (
    <Card id="utilization-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg">Utilization</CardTitle>
            <CardDescription>Weekly utilization (Utilized Hours / Available Hours) by worker type &amp; procurement source</CardDescription>
          </div>

          {/* Chart-level controls: Procurement Source */}
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {procurementSourceOptions.length > 0 && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Procurement Source</Label>
                <Select
                  value={selectedProcurementSource ?? 'all'}
                  onValueChange={v => onProcurementSourceChange(v === 'all' ? null : v)}
                >
                  <SelectTrigger className="h-8 w-[160px] text-xs">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {procurementSourceOptions.map(ps => (
                      <SelectItem key={ps} value={ps}>{ps}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <ChartExportMenu
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="space-y-2 h-[300px] flex flex-col justify-center">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-[260px] w-full" />
          </div>
        ) : !hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data found</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="week"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, (dataMax: number) => Math.max(100, Math.ceil(dataMax / 10) * 10)]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}%`}
                width={40}
              />
              <ReferenceLine y={80} stroke="#94a3b8" strokeDasharray="4 4" label={{ value: '80%', position: 'right', fontSize: 10, fill: '#94a3b8' }} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    valueFormatter={(value) => value == null ? 'N/A' : `${Number(value).toFixed(1)}%`}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
              {seriesKeys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  name={key}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
